import { useTranslation } from 'react-i18next';
import { Controller } from 'react-hook-form';
import { Info, Trash2 } from 'lucide-react';

import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Field, FieldGroup, FieldSet, FieldLabel } from '@/components/ui/field';

import { ConsignmentLineDetailResponse } from '@/entities/consignments';
import { FormField, DatePicker, SimpleSelect, FormSelect } from '@/shared/ui';
import { ConfirmationAction } from '@/features/confirmation-action';

import { useConsignmentLineDetailsForm } from '../model';
import { ConsignmentLineDetailsFormSkeleton } from './form-skeleton';
import { DensityValidationAlert } from './density-validation-alert';
import { OptionalFieldsSection } from './optional-fields-section';

interface ConsignmentLineDetailsFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (lineDetails: ConsignmentLineDetailResponse) => void;
  onDeleteSuccess?: () => void;
  lineId?: string;
  consignmentId?: string;
  mode?: 'create' | 'edit' | 'view';
  canDeleteLine?: boolean;
}

export const ConsignmentLineDetailsFormModal = ({
  isOpen,
  onClose,
  onSuccess,
  onDeleteSuccess,
  lineId,
  consignmentId,
  mode = 'view',
  canDeleteLine = false
}: ConsignmentLineDetailsFormModalProps) => {
  const { t } = useTranslation();

  const {
    form,
    productCategoryOptions,
    inventoryUnitOptions,
    handlingUnitOptions,
    dangerousGoodsOptions,
    conversionRateInfo,
    optionalFields,
    subProjectOptions,
    isLoading,
    isFetching,
    isSaving,
    isResetting,
    isDeleting,
    isViewMode,
    handleSubmit,
    handleDelete,
    handleClose
  } = useConsignmentLineDetailsForm({
    lineId,
    consignmentId,
    mode,
    onSuccess,
    onDeleteSuccess,
    onClose
  });

  const {
    control,
    formState: { errors, isDirty },
    register,
    watch
  } = form;

  const totalWeight = watch('totalWeight');
  const totalVolume = watch('totalVolume');

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent
        className="sm:max-w-4xl max-h-[80vh] overflow-y-auto"
        onInteractOutside={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle>{t('newcmr.linedetail.box')}</DialogTitle>
        </DialogHeader>

        <form
          id="consignment-line-details-form"
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleSubmit(e);
          }}
          className="py-4">
          {isLoading || isFetching || isResetting ? (
            <ConsignmentLineDetailsFormSkeleton />
          ) : (
            <div className="space-y-6">
              {/* Basic Information Section */}
              <FieldSet>
                <FieldGroup className="grid sm:grid-cols-2 gap-4">
                  <FormField label={t('product.description.label')} name="productCategory">
                    <FormSelect
                      name="productCategory"
                      control={control}
                      placeholder="Select category"
                      options={productCategoryOptions}
                    />
                  </FormField>

                  <FormField
                    label={t('requestline.ownerItemIdentifier.label')}
                    name="ownerReference"
                    error={!isViewMode ? errors.ownerReference?.message : undefined}>
                    <Input
                      {...register('ownerReference')}
                      id="ownerReference"
                      disabled={isViewMode}
                      placeholder={t('requestline.ownerItemIdentifier.desc')}
                    />
                  </FormField>
                </FieldGroup>

                <FieldGroup className="grid sm:grid-cols-2 gap-4">
                  <div className="flex flex-row gap-4">
                    <FormField
                      label={t('movement.ltuQty.label')}
                      name="inventoryQuantity"
                      error={!isViewMode ? errors.inventoryQuantity?.message : undefined}
                      className="w-full"
                      required>
                      <Input
                        {...register('inventoryQuantity', { valueAsNumber: true })}
                        type="number"
                        step="1"
                        min={0}
                        id="inventoryQuantity"
                        disabled={isViewMode}
                      />
                    </FormField>

                    <FormField label="" name="productLtuId">
                      <FormSelect
                        name="productLtuId"
                        control={control}
                        placeholder="Unit"
                        options={inventoryUnitOptions}
                      />
                    </FormField>
                  </div>

                  <FormField
                    label={t('requestline.expiryDate.label')}
                    name="expiryDate"
                    error={!isViewMode ? errors.expiryDate?.message : undefined}>
                    <Controller
                      name="expiryDate"
                      control={control}
                      render={({ field }) => (
                        <DatePicker
                          value={field.value ? new Date(field.value) : null}
                          onChange={(date) => field.onChange(date?.toISOString())}
                          dateFormat="dd/MMM/yyyy"
                          id="expiryDate"
                          disabled={isViewMode}
                        />
                      )}
                    />
                  </FormField>
                </FieldGroup>
              </FieldSet>

              <FormField
                label={t('requestline.ownerItemDesc.label')}
                name="description"
                error={!isViewMode ? errors.description?.message : undefined}
                required>
                <Controller
                  name="description"
                  control={control}
                  render={({ field }) =>
                    isViewMode ? (
                      <Input {...field} disabled={true} />
                    ) : (
                      <Textarea {...field} className="min-h-[80px]" placeholder={t('requestline.ownerItemDesc.desc')} />
                    )
                  }
                />
              </FormField>

              {/* Reference Information Section */}
              <FieldSet>
                <FieldGroup className="grid grid-cols-2 gap-4">
                  <FormField
                    label={t('requestline.batchNumber.label')}
                    name="batchNumber"
                    error={!isViewMode ? errors.batchNumber?.message : undefined}>
                    <Input
                      {...register('batchNumber')}
                      id="batchNumber"
                      disabled={isViewMode}
                      placeholder={t('requestline.batchNumber.label')}
                    />
                  </FormField>

                  {mode === 'view' && (
                    <FormField label={t('subproject.idnf.label')} name="subProjectCode">
                      <Input
                        {...register('subProjectCode')}
                        id="subProjectCode"
                        disabled={true}
                        placeholder={t('validity.valueMissing.select')}
                      />
                    </FormField>
                  )}
                </FieldGroup>
              </FieldSet>

              {subProjectOptions.length > 0 && (
                <FormField
                  label={t('subproject.idnf.label')}
                  name="subProjectCode"
                  required
                  error={!isViewMode ? errors.subProjectCode?.message : undefined}>
                  <FormSelect
                    name="subProjectCode"
                    control={control}
                    placeholder={t('validity.valueMissing.select')}
                    options={subProjectOptions}
                    disabled={isViewMode}
                  />
                </FormField>
              )}

              {/* Optional Fields Section */}
              <OptionalFieldsSection
                control={control}
                errors={errors}
                optionalFields={optionalFields}
                isViewMode={isViewMode}
              />

              {/* Handling Units Section */}
              <FieldSet>
                <FieldGroup className="grid sm:grid-cols-2 gap-4">
                  <div className="flex gap-4">
                    <FormField
                      label={t('bundle.bundleQty.label')}
                      name="handlingUnits"
                      error={!isViewMode ? errors.handlingUnits?.message : undefined}
                      className="w-full"
                      required>
                      <Input
                        {...register('handlingUnits', { valueAsNumber: true })}
                        type="number"
                        step="1"
                        id="handlingUnits"
                        disabled={isViewMode}
                      />
                    </FormField>

                    <FormField label="" name="handlingUnitTypeId">
                      <FormSelect
                        name="handlingUnitTypeId"
                        control={control}
                        placeholder="Unit"
                        options={handlingUnitOptions}
                      />
                    </FormField>
                  </div>
                </FieldGroup>
              </FieldSet>

              {/* Conversion Rate Info */}
              {conversionRateInfo && (
                <Alert>
                  <Info className="h-4 w-4" />
                  <AlertDescription>{conversionRateInfo}</AlertDescription>
                </Alert>
              )}

              {/* Physical Properties Section */}
              <FieldSet>
                <FieldGroup className="grid grid-cols-2 gap-4">
                  <FormField
                    label={t('tracking.servicereq.totweight.label')}
                    name="totalWeight"
                    error={!isViewMode ? errors.totalWeight?.message : undefined}
                    required>
                    <Input
                      {...register('totalWeight', { valueAsNumber: true })}
                      type="number"
                      step="0.01"
                      id="totalWeight"
                      disabled={isViewMode}
                    />
                  </FormField>

                  <FormField
                    label={t('tracking.servicereq.totalVolume.label')}
                    name="totalVolume"
                    error={!isViewMode ? errors.totalVolume?.message : undefined}
                    required>
                    <Input
                      {...register('totalVolume', { valueAsNumber: true })}
                      type="number"
                      step="0.01"
                      id="totalVolume"
                      disabled={isViewMode}
                    />
                  </FormField>
                </FieldGroup>

                {/* Volume/Weight Warning */}
                <DensityValidationAlert weight={totalWeight} volume={totalVolume} isViewMode={isViewMode} />

                <FieldGroup className="grid grid-cols-2 gap-4">
                  <FormField
                    label={t('requestline.insuranceValue.label')}
                    name="totalValue"
                    error={!isViewMode ? errors.totalValue?.message : undefined}>
                    <Input
                      {...register('totalValue', { valueAsNumber: true })}
                      type="number"
                      step="0.01"
                      id="totalValue"
                      disabled={isViewMode}
                      placeholder={t('requestline.insuranceValue.desc')}
                    />
                  </FormField>
                </FieldGroup>
              </FieldSet>

              {/* Shipping Flags Section */}
              <FieldSet>
                <FieldGroup className="grid grid-cols-2 gap-4">
                  <div className="space-y-4">
                    <Field orientation="horizontal">
                      <Controller
                        name="fragile"
                        control={control}
                        render={({ field }) => (
                          <Checkbox
                            id="fragile"
                            checked={field.value}
                            onCheckedChange={field.onChange}
                            disabled={isViewMode}
                          />
                        )}
                      />
                      <FieldLabel
                        htmlFor="fragile"
                        className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                        {t('requestline.isFragile.label')}
                      </FieldLabel>
                    </Field>

                    <Field orientation="horizontal">
                      <Controller
                        name="regulated"
                        control={control}
                        render={({ field }) => (
                          <Checkbox
                            id="regulated"
                            checked={field.value}
                            onCheckedChange={field.onChange}
                            disabled={isViewMode}
                          />
                        )}
                      />
                      <FieldLabel
                        htmlFor="regulated"
                        className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                        {t('requestline.isRegulated.label')}
                      </FieldLabel>
                    </Field>
                  </div>

                  <div className="space-y-4">
                    <Field orientation="horizontal">
                      <Controller
                        name="dangerous"
                        control={control}
                        render={({ field }) => (
                          <Checkbox
                            id="dangerous"
                            checked={field.value}
                            onCheckedChange={field.onChange}
                            disabled={isViewMode}
                          />
                        )}
                      />
                      <FieldLabel
                        htmlFor="dangerous"
                        className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                        {t('requestline.isDangerous.label')}
                      </FieldLabel>
                    </Field>

                    <Field orientation="horizontal">
                      <Controller
                        name="coldChain"
                        control={control}
                        render={({ field }) => (
                          <Checkbox
                            id="coldChain"
                            checked={field.value}
                            onCheckedChange={field.onChange}
                            disabled={isViewMode}
                          />
                        )}
                      />
                      <FieldLabel
                        htmlFor="coldChain"
                        className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                        {t('requestline.isColdStorage.label')}
                      </FieldLabel>
                    </Field>
                  </div>
                </FieldGroup>

                {/* Cold Chain Temperature Fields - shows when coldChain checkbox is checked */}
                {watch('coldChain') && (
                  <FieldGroup>
                    <FormField
                      label={t('requestline.coldMinTemp.label')}
                      name="coldMinTemp"
                      error={!isViewMode ? errors.coldMinTemp?.message : undefined}
                      required>
                      <Input
                        {...register('coldMinTemp', { valueAsNumber: true })}
                        type="number"
                        step="1"
                        id="coldMinTemp"
                        placeholder={t('requestline.coldMinTemp.placeholder', 'Enter minimum temperature')}
                        disabled={isViewMode}
                      />
                    </FormField>

                    <FormField
                      label={t('requestline.coldMaxTemp.label')}
                      name="coldMaxTemp"
                      error={!isViewMode ? errors.coldMaxTemp?.message : undefined}
                      required>
                      <Input
                        {...register('coldMaxTemp', { valueAsNumber: true })}
                        type="number"
                        step="1"
                        id="coldMaxTemp"
                        placeholder={t('requestline.coldMaxTemp.placeholder', 'Enter maximum temperature')}
                        disabled={isViewMode}
                      />
                    </FormField>
                  </FieldGroup>
                )}
                {/* Dangerous Goods Selector - shows when dangerous checkbox is checked */}
                {watch('dangerous') && (
                  <FormField
                    label={t('requestline.dangerousGoodsUnid.label')}
                    name="dangerousGoodsUnNumber"
                    error={!isViewMode ? errors.dangerousGoodsUnNumber?.message : undefined}>
                    <Controller
                      name="dangerousGoodsUnNumber"
                      control={control}
                      render={({ field }) => (
                        <SimpleSelect
                          options={dangerousGoodsOptions}
                          value={field.value}
                          onChange={field.onChange}
                          placeholder={t('gen.autocomplete.title')}
                          disabled={isViewMode}
                          className="w-full"
                        />
                      )}
                    />
                  </FormField>
                )}
              </FieldSet>

              {/* Handling Instructions */}
              <FormField
                label={t('requestline.handlingInstructions.label')}
                name="handlingInstructions"
                error={!isViewMode ? errors.handlingInstructions?.message : undefined}>
                <Controller
                  name="handlingInstructions"
                  control={control}
                  render={({ field }) =>
                    isViewMode ? <Input {...field} disabled={true} /> : <Textarea {...field} className="min-h-[80px]" />
                  }
                />
              </FormField>

              {/* Show on packing list */}
              <Field orientation="horizontal">
                <Controller
                  name="showOnPackingList"
                  control={control}
                  render={({ field }) => (
                    <Checkbox
                      id="showOnPackingList"
                      checked={field.value}
                      onCheckedChange={field.onChange}
                      disabled={isViewMode}
                    />
                  )}
                />
                <FieldLabel
                  htmlFor="showOnPackingList"
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                  {t('requestline.isOnPackingList.label')}
                </FieldLabel>
              </Field>
            </div>
          )}

          <div className="flex justify-between pt-6 mt-6 border-t">
            <div>
              {canDeleteLine && mode !== 'create' && (
                <ConfirmationAction
                  trigger={
                    <Button
                      type="button"
                      variant="destructive"
                      className="min-w-[120px]"
                      disabled={isSaving || isDeleting}>
                      <Trash2 className="h-4 w-4 mr-2" />
                      {t('gen.delete.label')}
                    </Button>
                  }
                  title={t('consignment.line.delete.title', 'Delete Consignment Line')}
                  description={t(
                    'consignment.line.delete.description',
                    'Are you sure you want to delete this consignment line? This action cannot be undone.'
                  )}
                  confirmText={t('gen.delete.label')}
                  cancelText={t('common.cancel')}
                  confirmVariant="destructive"
                  onConfirm={handleDelete}
                  isLoading={isDeleting}
                  loadingText={t('common.deleting', 'Deleting...')}
                />
              )}
            </div>
            <div className="flex gap-3">
              {isViewMode ? (
                <Button type="button" onClick={handleClose} className="min-w-[120px]">
                  {t('gen.closedialog.label')}
                </Button>
              ) : (
                <>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleClose}
                    className="min-w-[120px]"
                    disabled={isSaving}>
                    {t('gen.closedialog.label')}
                  </Button>
                  <Button
                    type="submit"
                    className="min-w-[120px]"
                    disabled={isSaving || !isDirty}
                    form="consignment-line-details-form">
                    {isSaving ? t('common.saving') : t('newcmr.save.label')}
                  </Button>
                </>
              )}
            </div>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
